import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import {
  ArrowRight, Lightbulb, AlertCircle, TrendingUp, Zap
} from "lucide-react";

const CSATLearnMode = ({ isPro, onBack, learningTopics, setLearningTopics }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [subtopicContent, setSubtopicContent] = useState(null);

  // Fetch topics when component mounts
  useEffect(() => {
    const fetchTopics = async () => {
      if (learningTopics.length > 0) return;
      
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/csat/learning/topics`);
        setLearningTopics(data.topics || []);
      } catch (err) {
        toast({ variant: "destructive", description: "Could not load topics." });
      } finally {
        setLoading(false);
      }
    };
    
    fetchTopics();
  }, [learningTopics.length, setLearningTopics, toast]);

  // Fetch subtopic content
  const fetchSubtopicContent = async (topicId, subtopicId) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/csat/learning/topic/${topicId}/subtopic/${subtopicId}`);
      setSubtopicContent(data);
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load content." });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedTopic(null);
    setSelectedSubtopic(null);
    setSubtopicContent(null);
    onBack();
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={handleBack}>
            ← Back to CSAT Suite
          </Button>
          <Badge variant="outline" className={`px-4 py-2 text-sm ${isPro ? 'border-slate-900 text-slate-900' : 'border-slate-300 text-slate-500'}`}>
            {isPro ? "PRO" : "FREE"}
          </Badge>
        </div>

        {/* Header - CLARIAS Style */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900">Learn CSAT</h1>
          <p className="text-slate-500">
            Master concepts • Learn shortcuts • Practice with examples
          </p>
        </div>

        {/* If viewing subtopic content */}
        {subtopicContent ? (
          <div className="space-y-6">
            <Button variant="outline" onClick={() => setSubtopicContent(null)}>
              ← Back to Topics
            </Button>

            <Card className="border-slate-200 bg-slate-50/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-slate-900">
                    {subtopicContent.topic_name} → {subtopicContent.subtopic_name}
                  </h2>
                  <Badge className={`${
                    subtopicContent.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                    subtopicContent.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {subtopicContent.difficulty.toUpperCase()}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Concepts */}
            {subtopicContent.concepts && subtopicContent.concepts.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-600" />
                    Core Concepts
                  </h3>
                  <div className="space-y-4">
                    {subtopicContent.concepts.map((concept, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">{concept.title}</h4>
                        <p className="text-sm text-slate-700 mb-2">{concept.explanation}</p>
                        {concept.formula && (
                          <div className="bg-white border border-purple-200 rounded px-3 py-2 mb-2">
                            <p className="text-sm font-mono text-purple-800">{concept.formula}</p>
                          </div>
                        )}
                        {concept.example && (
                          <p className="text-xs text-slate-600 italic">📝 Example: {concept.example}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shortcuts */}
            {subtopicContent.shortcuts && subtopicContent.shortcuts.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-orange-600" />
                    Quick Shortcuts
                  </h3>
                  <ul className="space-y-2">
                    {subtopicContent.shortcuts.map((shortcut, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-orange-600 mt-0.5">⚡</span>
                        <span className="text-sm text-slate-700">{shortcut}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Common Mistakes (PRO) */}
            {subtopicContent.common_mistakes && subtopicContent.common_mistakes.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    Common Mistakes to Avoid
                  </h3>
                  <ul className="space-y-2">
                    {subtopicContent.common_mistakes.map((mistake, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-red-600 mt-0.5">⚠️</span>
                        <span className="text-sm text-slate-700">{mistake}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* PYQ Insights (PRO) */}
            {subtopicContent.pyq_insights && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                    PYQ Insights
                  </h3>
                  <p className="text-sm text-slate-700">{subtopicContent.pyq_insights}</p>
                </CardContent>
              </Card>
            )}

            {/* Paywall Message for FREE users */}
            {subtopicContent.paywall_message && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-orange-800 mb-4">{subtopicContent.paywall_message}</p>
                  <Button className="bg-orange-600 hover:bg-orange-700">
                    Upgrade to PRO
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        ) : selectedTopic ? (
          // Show subtopics of selected topic
          <div className="space-y-4">
            <Button variant="outline" onClick={() => setSelectedTopic(null)}>
              ← Back to All Topics
            </Button>

            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{selectedTopic.name}</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {selectedTopic.subtopics.map((subtopic) => (
                    <Card 
                      key={subtopic.id} 
                      className="cursor-pointer hover:shadow-lg transition-all border-purple-200"
                      onClick={() => {
                        setSelectedSubtopic(subtopic);
                        fetchSubtopicContent(selectedTopic.id, subtopic.id);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-slate-900">{subtopic.name}</h3>
                          <Badge className={`${
                            subtopic.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                            subtopic.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {subtopic.difficulty}
                          </Badge>
                        </div>
                        <Button size="sm" variant="outline" className="w-full mt-2">
                          Start Learning →
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          // Show all topics - CLARIAS Style
          <div className="grid gap-4 md:grid-cols-2">
            {loading ? (
              <p className="text-center text-slate-600 col-span-2">Loading topics...</p>
            ) : (
              learningTopics.map((topic) => (
                <Card 
                  key={topic.id} 
                  className="group cursor-pointer border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
                  onClick={() => setSelectedTopic(topic)}
                >
                  <CardContent className="p-5">
                    <h3 className="font-medium text-slate-900 mb-2">{topic.name}</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      {topic.subtopic_count} subtopic{topic.subtopic_count !== 1 ? 's' : ''}
                    </p>
                    <div className="flex items-center text-xs text-slate-400 group-hover:text-slate-600">
                      Explore topics
                      <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSATLearnMode;
